import React, { useEffect, useState } from 'react';
import { IonRouterLink, IonIcon, IonSkeletonText, IonBadge } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { api } from '@/utils/api';
import { golf } from 'ionicons/icons';
import { getScoreClass } from '@/utils/scoreUtils';

interface GameResultPreviewProps {
  gameId: number;
  className?: string;
  userId?: string;
}

interface GameData {
  name: string;
  clubName: string;
  courseName: string;
  date: string;
  userId: number;
  par: number;
  toPar: number;
  holes: number;
  holeScores?: Record<string, Record<string, any>>;
  holesData?: Array<{ hole: number; par: number; distance: number }>;
  teeColor?: string;
  teeDistance?: string;
  format?: string;
  playerId?: string;
}

const GameResultMedia: React.FC<GameResultPreviewProps> = ({ gameId, className, userId }) => {
  const { t } = useTranslation();
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/game/${gameId}/results?userId=${userId}`);

        if (response.data && response.data.players && response.data.players.length > 0) {
          const player = response.data.players[0]; // Get first player's data
          const teeId = Object.keys(response.data.holesByTee || {})[0]; // Get first tee ID

          // Extract hole data with proper par values
          const holesData = teeId && response.data.holesByTee ?
            response.data.holesByTee[teeId].map(hole => ({
              hole: hole.holeNumber,
              par: hole.par || 4, // Default to par 4 if not specified
              distance: hole.distance || 0
            })) : [];

          // Process scores data
          const processedScores = {};
          if (response.data.scores) {
            Object.entries(response.data.scores).forEach(([holeNum, playerScores]) => {
              processedScores[holeNum] = playerScores;
            });
          }

          const gameInfo = {
            name: response.data.game.name || '',
            clubName: response.data.game.clubName || '',
            courseName: response.data.game.courseName || '',
            userId: response.data.game.userId,
            date: formatDate(response.data.game.date),
            par: response.data.totalPar,
            toPar: player.toPar || 0,
            holes: response.data.game.holes,
            holeScores: processedScores,
            holesData: holesData,
            teeColor: response.data.teeColor || 'white',
            teeDistance: response.data.teeDistance || '',
            format: response.data.format || 'Strokeplay',
            playerId: Object.keys(processedScores[1] || {})[0] // Get first player ID
          };
          setGameData(gameInfo);
        }
      } catch (err) {
        console.error('Error fetching game data:', err);
        setError(t('error_loading_game'), err);
      } finally {
        setLoading(false);
      }
    };

    if (gameId && !gameData) {
      fetchGameData();
    }
  }, [gameId]);

  if (loading) {
    return (
      <div className={cn("relative w-full rounded-xl overflow-hidden bg-card shadow-md", className)}>
        <IonSkeletonText animated style={{ height: '240px', width: '100%' }} />
      </div>
    );
  }

  if (error || !gameData) {
    console.log('Error or no game data:', error, gameData);
    return (
      <div className={cn("relative w-full rounded-xl overflow-hidden bg-card shadow-md p-4 text-center", className)}>
        <IonIcon icon={golf} className="w-10 h-10 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">{error || t('no_game_data')}</p>
      </div>
    );
  }

  return (
    <IonRouterLink routerLink={`/game/${gameId}/result`} className="w-full block">
      <div className={cn("relative w-full rounded-xl overflow-hidden bg-card shadow-md", className)}>
        {/* Background image */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/40 z-10"></div>
        <div className="absolute inset-0 bg-cover bg-center z-0"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=3270&auto=format&fit=crop)' }}>
        </div>

        {/* Content overlay */}
        <div className="relative z-20 p-4 text-white flex justify-between h-full min-h-[240px]">
          <div className="flex flex-col justify-end">
            {/* Date */}
            <div className="font-bold">{gameData.date}</div>
            {/* Course Name */}
            <h3 className="text-xl font-bold">{gameData.clubName}</h3>
            <h4 className="text-md font-medium mb-2">{gameData.courseName} - {gameData.teeColor}</h4>

            {/* Course details */}
            <div className="text-sm space-x-1">
              <IonBadge color="secondary"><span>{gameData.holes}{t('holes')}</span></IonBadge>
              <IonBadge color="primary">{gameData.format}</IonBadge>
              <IonBadge color="primary">{gameData.par}</IonBadge>
            </div>
          </div>

          {/* Score display */}
          <div className="mt-auto flex justify-between items-end">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2 text-white inline-block">
              <div className="relative inline-flex items-start">
                <span className="text-5xl font-bold">
                  {gameData?.holeScores ? Object.keys(gameData.holeScores)
                    .reduce((total, hole) => {
                      const score = gameData.playerId && gameData.holeScores && gameData.holeScores[hole] &&
                        gameData.holeScores[hole][gameData.playerId || '']?.score;
                      return total + (score || 0);
                    }, 0) : '-'}
                </span>
                {gameData?.holeScores && gameData.par ? (() => {
                  const totalScore = Object.keys(gameData.holeScores).reduce((total, hole) => {
                    const score = gameData.playerId && gameData.holeScores && gameData.holeScores[hole] &&
                      gameData.holeScores[hole][gameData.playerId || '']?.score;
                    return total + (score || 0);
                  }, 0);

                  const relativeToPar = totalScore - gameData.par;
                  const relativeText = relativeToPar > 0 ? `+${relativeToPar}` : relativeToPar;

                  return (
                    <span className={`text-xl font-bold -translate-y-2`}>
                      {relativeText}
                    </span>
                  );
                })() : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Hole score display */}
        <div className="relative z-20 bg-black/20 backdrop-blur-sm p-2">
          {/* Front Nine (Holes 1-9) */}
          {/* Front Nine Label */}
          <div className="flex justify-between">
            {/* Hole numbers */}
            <div className="flex-1 flex justify-between">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(hole => (
                <div key={`hole-${hole}`} className="w-8 text-sm text-center text-white">{hole}</div>
              ))}
            </div>
            <div className="w-16 text-center text-white font-medium">OUT</div>
          </div>

          {/* Front Nine Scores */}
          <div className="flex justify-between mt-1">
            <div className="flex-1 flex justify-between">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(hole => {
                // Get the hole data with par information
                const holeInfo = gameData?.holesData?.[hole];
                // Get the player's score for this hole
                const playerScore = gameData?.playerId && gameData?.holeScores &&
                  gameData.holeScores[hole] && gameData.holeScores[hole][gameData.playerId];

                return (
                  <div key={`score-${hole}`} className={`w-8 text-center justify-center font-bold ${getScoreClass(playerScore?.score || 0, holeInfo?.par || 4)} border-none text-gray-400'}`}>
                    {playerScore ? playerScore.score : '-'}
                  </div>
                );
              })}
            </div>
            <div className="w-16 text-center text-white font-medium">
              {gameData?.holeScores ? Object.keys(gameData.holeScores)
                .filter(hole => parseInt(hole) <= 9)
                .reduce((total, hole) => {
                  const score = gameData.playerId && gameData.holeScores && gameData.holeScores[hole] &&
                    gameData.holeScores[hole][gameData.playerId || '']?.score;
                  return total + (score || 0);
                }, 0) : '-'}
            </div>
          </div>

          {/* Back Nine (Holes 10-18) - Only show if we have more than 9 holes */}
          {gameData.holes > 9 && (
            <div className='border-t mt-2 pt-2'>
              {/* Back Nine Label */}
              <div className="flex justify-between">
                {/* Hole numbers */}
                <div className="flex-1 flex justify-between">
                  {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(hole => (
                    <div key={`hole-${hole}`} className="w-8 text-sm text-center text-white">{hole}</div>
                  ))}
                </div>
                <div className="w-16 text-center text-white font-medium">IN</div>
              </div>

              {/* Back Nine Scores */}
              <div className="flex justify-between mt-1">
                <div className="flex-1 flex justify-between">
                  {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(hole => {
                    // Get the hole data with par information
                    const holeInfo = gameData?.holesData?.[hole];
                    // Get the player's score for this hole
                    const playerScore = gameData?.playerId && gameData?.holeScores &&
                      gameData.holeScores[hole] && gameData.holeScores[hole][gameData.playerId];

                    return (
                      <div key={`score-${hole}`} className={`w-8 text-center justify-center font-bold ${getScoreClass(playerScore?.score || 0, holeInfo?.par || 4)} border-none text-gray-400'}`}>
                        {playerScore ? playerScore.score : '-'}
                      </div>
                    );
                  })}
                </div>
                {/* Back nine total */}
                <div className="w-16 text-center text-white font-bold">
                  {gameData?.holeScores ? Object.keys(gameData.holeScores)
                    .filter(hole => parseInt(hole) > 9 && parseInt(hole) <= 18)
                    .reduce((total, hole) => {
                      const score = gameData.playerId && gameData.holeScores && gameData.holeScores[hole] &&
                        gameData.holeScores[hole][gameData.playerId || '']?.score;
                      return total + (score || 0);
                    }, 0) : '-'}
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </IonRouterLink>
  );
};

export default GameResultMedia;
